import safeGet from 'just-safe-get'

import { useAuthStore } from '@shared/store/auth'
import { getChildNameByPermissions } from '@shared/utils/navigation'

import { requireAuth, noMatch, commonRoutes } from '@shared/router/helper'

import { useAppConfigStore } from '@shared/store/app-config'
import { useConfigurationStore } from '@shared/store/configuration'
import { FeatureFlags } from '@shared/plugins/posthog/feature-flags'

/**
 * Route `meta` carries three independent access checks, all enforced by the global guard in
 * `shared/router/index.js`:
 * - `scopes`      — the user's permissions; redirects to /403.
 * - `featurePath` — account entitlement, from the server driven `featureConfig`.
 * - `rolloutFlag` — rollout gating, resolved through Posthog; redirects to /404 until the flag resolves enabled
 *   for the merchant. Declaring a key from the `FeatureFlags` registry is all that is needed.
 *   Put it on the parent record and it covers every child route. Hide the matching nav entry
 *   with the same key via `rolloutFlag` in `th-navigation.js`.
 */
const Defaulter = () =>
  import(/* webpackChunkName: "defaulter" */ '@shared/components/defaulter')

const CustomDashboardsWrap = () =>
  import(
    /* webpackChunkName: "custom-dashboards" */ '@th/views/utilities/custom-dashboards/components/custom-content'
  )

const routes = [
  {
    path: '',
    redirect: '/home'
  },
  {
    path: '/home',
    beforeEnter: async () => {
      await useAuthStore().waitThInitializated()
      // root is special as we don't want to show 401 error, so we go straight to login
      if (!useAuthStore().isAuthenticated) {
        return '/login'
      }
    },
    children: [
      {
        path: '',
        name: 'home',
        component: () =>
          import(/* webpackChunkName: "home" */ '@th/views/home/new')
      },
      {
        path: 'old',
        name: 'home-old',
        component: () =>
          import(/* webpackChunkName: "home-old" */ '@th/views/home/main')
      }
    ]
  },
  {
    path: '/custom_dashboards/:name',
    name: 'root-custom-dashboards',
    component: CustomDashboardsWrap,
    beforeEnter: requireAuth,
    meta: {
      scopes: ['utilities:custom_dashboard:read']
    }
  },
  {
    path: '/customers',
    component: () =>
      import(/* webpackChunkName: "customers" */ '@th/views/customers'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        name: 'customers-list',
        component: () =>
          import(
            /* webpackChunkName: "customers-all" */ '@th/views/customers/all'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['customers:read']
        }
      },
      {
        path: 'new',
        name: 'customer-new',
        component: () =>
          import(
            /* webpackChunkName: "customer_edit" */ '@th/views/customers/edit'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['customers:create']
        }
      },
      {
        path: ':id',
        component: () =>
          import(
            /* webpackChunkName: "customer_edit" */ '@th/views/customers/edit'
          ),
        beforeEnter: requireAuth
      },
      noMatch
    ]
  },
  {
    path: '/supplier-management',
    component: () =>
      import(
        /* webpackChunkName: "supplier_management" */ '@th/views/supplier-management'
      ),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/supplier-management'])
      },
      {
        path: 'suppliers',
        component: () =>
          import(
            /* webpackChunkName: "supplier_management_suppliers" */ '@th/views/supplier-management'
          ),
        beforeEnter: requireAuth,
        children: [
          {
            path: '',
            name: 'suppliers-list',
            component: () =>
              import(
                /* webpackChunkName: "supplier_management_suppliers_all" */ '@th/views/supplier-management/suppliers/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['supplier_management:suppliers:read']
            }
          },
          {
            path: 'new',
            name: 'supplier-new',
            component: () =>
              import(
                /* webpackChunkName: "supplier_management_supplier_edit" */ '@th/views/supplier-management/suppliers/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['supplier_management:suppliers:create']
            }
          },
          {
            path: ':id',
            component: () =>
              import(
                /* webpackChunkName: "supplier_management_supplier_edit" */ '@th/views/supplier-management/suppliers/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['supplier_management:suppliers:update']
            }
          },
          noMatch
        ]
      },
      {
        path: 'purchase-orders',
        component: () =>
          import(
            /* webpackChunkName: "supplier_management_purchase_orders" */ '@th/views/supplier-management/purchase-orders'
          ),
        beforeEnter: requireAuth,
        children: [
          {
            path: '',
            name: 'purchase-orders-list',
            component: () =>
              import(
                /* webpackChunkName: "supplier_management_purchase_orders_all" */ '@th/views/supplier-management/purchase-orders/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['supplier_management:purchase_orders:read']
            }
          },
          {
            path: 'new',
            name: 'purchase-orders-new',
            component: () =>
              import(
                /* webpackChunkName: "supplier_management_purchase_order_new" */ '@th/views/supplier-management/purchase-orders/edit.vue'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['supplier_management:purchase_orders:create'],
              title: 'Purchase order'
            }
          },
          {
            path: ':id',
            component: () =>
              import(
                /* webpackChunkName: "supplier_management_purchase_order_edit" */ '@th/views/supplier-management/purchase-orders/edit.vue'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['supplier_management:purchase_orders:update'],
              title: 'Purchase order'
            }
          },
          noMatch
        ]
      }
    ]
  },
  {
    path: '/documents',
    component: () =>
      import(/* webpackChunkName: "documents" */ '@th/views/documents'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/documents'])
      },
      {
        path: 'overview',
        component: () =>
          import(
            /* webpackChunkName: "documents_overview" */ '@th/views/documents/overview'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['documents:read']
        }
      }
    ]
  },
  {
    path: '/resources',
    component: () =>
      import(/* webpackChunkName: "resources" */ '@th/views/resources'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/resources'])
      },
      {
        path: 'branches',
        component: () =>
          import(
            /* webpackChunkName: "resources_branches" */ '@th/views/resources/branches'
          ),
        meta: {
          scopes: ['resources:branches:read'],
          featurePath: 'resources.branches'
        },
        children: [
          {
            path: '',
            name: 'resources-branches',
            component: () =>
              import(
                /* webpackChunkName: "branches_all" */ '@th/views/resources/branches/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:branches:read']
            }
          },
          {
            path: 'new',
            name: 'resources-branches-new',
            component: () =>
              import(
                /* webpackChunkName: "branches_edit" */ '@th/views/resources/branches/edit'
              ),
            meta: {
              scopes: ['resources:branches:create']
            }
          },
          {
            path: ':id',
            name: 'resources-branches-edit',
            component: () =>
              import(
                /* webpackChunkName: "branches_edit" */ '@th/views/resources/branches/edit'
              ),
            meta: {
              scopes: ['resources:branches:read']
            }
          }
        ]
      },
      {
        path: 'branch_groups',
        component: () =>
          import(
            /* webpackChunkName: "resources_branch-groups" */ '@th/views/resources/branch-groups'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['resources:branch_groups:read'],
          featurePath: 'resources.branch_groups'
        },
        children: [
          {
            path: '',
            name: 'branch-groups-list',
            component: () =>
              import(
                /* webpackChunkName: "branch-groups" */ '@th/views/resources/branch-groups/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:branch_groups:read']
            }
          },
          {
            path: 'new',
            name: 'branch-group-new',
            component: () =>
              import(
                /* webpackChunkName: "branch-groups_edit" */ '@th/views/resources/branch-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:branch_groups:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'branch-group-edit' }
          },
          {
            path: ':id/edit',
            name: 'branch-group-edit',
            component: () =>
              import(
                /* webpackChunkName: "branch-groups_edit" */ '@th/views/resources/branch-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:branch_groups:update']
            }
          },
          noMatch
        ]
      },
      {
        path: 'warehouses',
        component: () =>
          import(/* webpackChunkName: "warehouses" */ '@th/views/warehouses'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['resources:warehouses:read'],
          featurePath: 'resources.warehouses'
        },
        children: [
          {
            path: '',
            name: 'warehouses-list',
            component: () =>
              import(
                /* webpackChunkName: "warehouses" */ '@th/views/warehouses/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:warehouses:read']
            }
          },
          {
            path: 'new',
            name: 'warehouses-new',
            component: () =>
              import(
                /* webpackChunkName: "warehouses_edit" */ '@th/views/warehouses/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:warehouses:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'warehouses-edit' }
          },
          {
            path: ':id/edit',
            name: 'warehouses-edit',
            component: () =>
              import(
                /* webpackChunkName: "warehouses_edit" */ '@th/views/warehouses/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:warehouses:update']
            }
          },
          noMatch
        ]
      },
      {
        path: 'registers',
        component: Defaulter,
        beforeEnter: requireAuth,
        meta: {
          scopes: ['resources:registers:read'],
          featurePath: 'resources.registers'
        },
        children: [
          {
            path: '',
            name: 'resources-registers-all',
            component: () =>
              import(
                /* webpackChunkName: "registers_all" */ '@th/views/registers/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:registers:read']
            }
          },
          {
            path: ':id',
            name: 'resources-registers-edit',
            component: () =>
              import(
                /* webpackChunkName: "registers_edit" */ '@th/views/registers/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:registers:read']
            }
          }
        ]
      },
      {
        path: 'licenses',
        component: () =>
          import(/* webpackChunkName: "licenses" */ '@th/views/licenses'),
        meta: {
          scopes: ['resources:licenses:read'],
          featurePath: 'resources.licenses'
        },
        children: [
          {
            path: '',
            name: 'resources-licenses',
            component: () =>
              import(
                /* webpackChunkName: "licenses_all" */ '@th/views/licenses/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:licenses:read']
            }
          }
        ]
      },
      {
        path: 'devices',
        component: () =>
          import(/* webpackChunkName: "devices" */ '@th/views/devices'),
        meta: {
          scopes: ['resources:devices:read'],
          featurePath: 'resources.devices'
        },
        children: [
          {
            path: '',
            name: 'resources-devices',
            component: () =>
              import(
                /* webpackChunkName: "devices_all" */ '@th/views/devices/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:devices:read']
            }
          },
          {
            path: 'new',
            name: 'resources-devices-new',
            component: () =>
              import(
                /* webpackChunkName: "devices_edit" */ '@th/views/devices/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:devices:create']
            }
          },
          {
            path: ':id',
            name: 'resources-devices-edit',
            component: () =>
              import(
                /* webpackChunkName: "devices_edit" */ '@th/views/devices/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:devices:update']
            }
          }
        ]
      },
      {
        path: 'device_groups',
        component: () =>
          import(
            /* webpackChunkName: "device_groups" */ '@th/views/device-groups'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['resources:device_groups:read'],
          featurePath: 'resources.device_groups'
        },
        children: [
          {
            path: '',
            name: 'device-groups-list',
            component: () =>
              import(
                /* webpackChunkName: "device-groups" */ '@th/views/device-groups/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:device_groups:read']
            }
          },
          {
            path: 'new',
            name: 'device-group-new',
            component: () =>
              import(
                /* webpackChunkName: "device-groups_edit" */ '@th/views/device-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:device_groups:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'device-group-edit' }
          },
          {
            path: ':id/edit',
            name: 'device-group-edit',
            component: () =>
              import(
                /* webpackChunkName: "device-groups_edit" */ '@th/views/device-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['resources:device_groups:update']
            }
          },
          noMatch
        ]
      }
    ]
  },
  {
    path: '/livehubs',
    component: () => import('@th/views/livehubs/index.vue'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/livehubs'])
      },
      {
        path: 'devices',
        component: () => import('@th/views/livehubs/devices/index.vue'),
        meta: {
          scopes: ['livehubs:livehub_devices:read'],
          featurePath: 'resources.devices'
        },
        children: [
          {
            name: 'livehubs-devices-list',
            path: '',
            component: () => import('@th/views/livehubs/devices/all.vue'),
            meta: {
              scopes: ['livehubs:livehub_devices:read'],
              featurePath: 'resources.devices'
            }
          },
          {
            path: ':id/edit',
            name: 'livehubs-devices-edit',
            component: () => import('@th/views/livehubs/devices/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['livehubs:livehub_devices:update'],
              featurePath: 'resources.devices'
            }
          },
          {
            path: ':id',
            redirect: { name: 'livehubs-devices-edit' }
          },
          noMatch
        ]
      },
      {
        path: 'media',
        component: () => import('@th/views/livehubs/media/index.vue'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['utilities:cms:read'],
          featurePath: 'utilities.cms.content'
        },
        children: [
          {
            path: '',
            name: 'livehubs-media-list',
            component: () => import('@th/views/livehubs/media/all.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:cms:read']
            }
          },
          {
            path: 'new',
            name: 'livehubs-media-new',
            component: () => import('@th/views/livehubs/media/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:cms:create']
            }
          },
          {
            path: ':id',
            name: 'livehubs-media-edit',
            component: () => import('@th/views/livehubs/media/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:cms:update']
            }
          }
        ]
      },
      {
        path: 'templates',
        component: () => import('@th/views/livehubs/templates/index.vue'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['utilities:cms:read'],
          featurePath: 'utilities.cms.content_templates'
        },
        children: [
          {
            path: '',
            name: 'livehubs-templates-list',
            component: () => import('@th/views/livehubs/templates/all.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:cms:read']
            }
          },
          {
            path: 'new',
            name: 'livehubs-templates-new',
            component: () => import('@th/views/livehubs/templates/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:cms:create']
            }
          },
          {
            path: ':id',
            name: 'livehubs-templates-edit',
            component: () => import('@th/views/livehubs/templates/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:cms:update']
            }
          }
        ]
      },
      noMatch
    ]
  },
  {
    path: '/utilities',
    component: () => import('@th/views/utilities'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/utilities'])
      },
      {
        path: 'audits',
        component: () =>
          import(
            /* webpackChunkName: "utilities_audits" */ '@th/views/utilities/audits'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['utilities:audits']
        },
        children: [
          {
            path: '',
            redirect: () => getParentRedirectTo(['/utilities', 'audits'])
          },
          {
            path: 'actions',
            name: 'utilities-audits-actions',
            component: () =>
              import(
                /* webpackChunkName: "settings_customers" */ '@th/views/utilities/audits/actions'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:audits'],
              featurePath: 'utilities.audits.actions'
            }
          },
          {
            path: 'logs',
            name: 'utilities-audits-logs',
            component: () =>
              import(
                /* webpackChunkName: "settings_customers" */ '@th/views/utilities/audits/logs'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:audits'],
              featurePath: 'utilities.audits.logs'
            }
          },
          noMatch
        ]
      },
      {
        path: 'tse',
        component: () => import('@th/views/utilities/tse/index.vue'),
        beforeEnter: requireAuth,
        meta: {
          featurePath: 'utilities.kassensichv_submissions'
        },
        children: [
          {
            path: '',
            name: 'tse-list',
            component: () => import('@th/views/utilities/tse/all.vue'),
            beforeEnter: requireAuth
          },
          {
            path: ':branch',
            redirect: { name: 'tse-edit' }
          },
          {
            path: ':branch/edit',
            name: 'tse-edit',
            component: () => import('@th/views/utilities/tse/edit.vue')
          },
          noMatch
        ]
      },
      {
        path: 'document_designer',
        component: () =>
          import(
            /* webpackChunkName: "templater" */ '@th/views/utilities/templater/templater'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['utilities:document_designer:read'],
          featurePath: 'utilities.document_designer'
        },
        children: [
          {
            path: '',
            redirect: () =>
              getParentRedirectTo(['/utilities', 'document_designer'])
          },
          {
            path: 'invoices',
            name: 'invoices',
            component: () =>
              import(
                /* webpackChunkName: "invoices" */ '@th/views/utilities/templater/invoices'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:document_designer:read']
            }
          },
          {
            path: 'full_receipts',
            name: 'full_receipts',
            component: () =>
              import(
                /* webpackChunkName: "full_receipts" */ '@th/views/utilities/templater/full-receipts'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:document_designer:read']
            }
          },
          {
            path: 'delivery_notes',
            name: 'delivery_notes',
            component: () =>
              import(
                /* webpackChunkName: "delivery_notes" */ '@th/views/utilities/templater/delivery-notes'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:document_designer:read']
            }
          }
        ]
      },
      {
        path: 'custom_dashboards',
        beforeEnter: requireAuth,
        meta: {
          scopes: ['utilities:custom_dashboard:read'],
          featurePath: 'utilities.custom_dashboards'
        },
        children: [
          {
            path: '',
            name: 'utilities-custom-dashboards',
            component: () =>
              import(
                /* webpackChunkName: "utilities_custom-dashboards" */ '@th/views/utilities/custom-dashboards'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:custom_dashboard:read']
            }
          },
          {
            path: ':name',
            component: CustomDashboardsWrap,
            meta: {
              scopes: ['utilities:custom_dashboard:read']
            }
          },
          noMatch
        ]
      },
      {
        path: 'pos_functions',
        component: () =>
          import(
            /* webpackChunkName: "pos_functions" */ '@th/views/utilities/pos-functions'
          ),
        beforeEnter: requireAuth,
        children: [
          {
            path: '',
            name: 'pos-functions',
            component: () =>
              import(
                /* webpackChunkName: "pos_functions_all" */ '@th/views/utilities/pos-functions/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:pos_functions:read'],
              featurePath: 'utilities.pos_functions'
            }
          },
          {
            path: 'new',
            name: 'pos-functions-new',
            component: () =>
              import(
                /* webpackChunkName: "pos_functions_edit" */ '@th/views/utilities/pos-functions/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: [
                'utilities:pos_functions:read',
                'resources:pos_functions:create'
              ]
            }
          },
          {
            path: ':id',
            redirect: { name: 'pos-functions-edit' }
          },
          {
            path: ':id/edit',
            name: 'pos-functions-edit',
            component: () =>
              import(
                /* webpackChunkName: "pos_functions_edit" */ '@th/views/utilities/pos-functions/edit'
              ),
            meta: {
              scopes: [
                'utilities:pos_functions:read',
                'utilities:pos_functions:create',
                'utilities:pos_functions:update'
              ],
              featurePath: 'utilities.pos_functions'
            }
          },
          noMatch
        ]
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: () =>
          import(
            /* webpackChunkName: "device_notifications" */ '@th/views/utilities/device-notifications'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['utilities:notifications:create'],
          featurePath: 'utilities.notifications'
        }
      },
      {
        path: 'favourites',
        name: 'favourites',
        component: () =>
          import(
            /* webpackChunkName: "favourites_editor" */ '@th/views/utilities/favourites/favourites'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: [
            'utilities:favourites:create',
            'utilities:favourites:update'
          ],
          featurePath: 'utilities.favourites'
        }
      },
      {
        path: 'custom_dashboards/standalone/:name',
        name: 'utilities-custom-dashboards-standalone',
        component: CustomDashboardsWrap,
        beforeEnter: requireAuth,
        meta: {
          scopes: ['utilities:custom_dashboard:read']
        },
        props: { standalone: true }
      },
      {
        path: 'export_history',
        name: 'export-history',
        component: () =>
          import(
            /* webpackChunkName: "utilities_export-history" */ '@th/views/utilities/export-history'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['utilities:export_history']
        }
      },
      noMatch
    ]
  },
  {
    path: '/staff',
    component: () => import(/* webpackChunkName: "staff" */ '@th/views/staff'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/staff']),
        meta: {
          scopes: ['staff:read'],
          featurePath: 'staff'
        }
      },
      {
        path: 'manager',
        component: () =>
          import(/* webpackChunkName: "staff" */ '@th/views/staff'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['staff:read']
        },
        children: [
          {
            path: '',
            name: 'staff-list',
            component: () =>
              import(/* webpackChunkName: "staff" */ '@th/views/staff/all'),
            meta: {
              scopes: ['staff:read']
            }
          },
          {
            path: 'new',
            name: 'staff-new',
            component: () =>
              import(
                /* webpackChunkName: "staff_new" */ '@th/views/staff/edit'
              ),
            meta: {
              scopes: ['staff:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'staff-edit' }
          },
          {
            path: ':id/edit',
            name: 'staff-edit',
            component: () =>
              import(
                /* webpackChunkName: "staff_edit" */ '@th/views/staff/edit'
              ),
            meta: {
              scopes: ['staff:update']
            }
          }
        ]
      },
      {
        path: 'staff_groups',
        component: () =>
          import(
            /* webpackChunkName: "staff-groups" */ '@th/views/staff-groups'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['staff:groups:read']
        },
        children: [
          {
            path: '',
            name: 'staff-groups-list',
            component: () =>
              import(
                /* webpackChunkName: "staff-groups" */ '@th/views/staff-groups/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['staff:groups:read']
            }
          },
          {
            path: 'new',
            name: 'staff-group-new',
            component: () =>
              import(
                /* webpackChunkName: "staff-groups_edit" */ '@th/views/staff-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['staff:groups:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'staff-group-edit' }
          },
          {
            path: ':id/edit',
            name: 'staff-group-edit',
            component: () =>
              import(
                /* webpackChunkName: "staff-groups_edit" */ '@th/views/staff-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['staff:groups:update']
            }
          },
          noMatch
        ]
      },
      {
        path: 'shift_plan',
        name: 'shift-plan',
        component: () => import('@th/views/shift-plan/index.vue'),
        meta: {
          scopes: ['staff:shift_plans:read']
        },
        children: [
          {
            path: '',
            name: 'shift-plan-list',
            component: () => import('@th/views/shift-plan/all.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['staff:shift_plans:read']
            }
          },
          {
            path: ':branch',
            redirect: { name: 'shift-plan-edit' }
          },
          {
            path: ':branch/edit',
            name: 'shift-plan-edit',
            component: () => import('@th/views/shift-plan/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['staff:shift_plans:read']
            }
          },
          noMatch
        ]
      },
      noMatch
    ]
  },
  {
    path: '/reservations',
    component: () => import('@th/views/reservations'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/reservations'])
      },
      {
        path: 'service_category',
        component: () => import('@th/views/reservations'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['reservations:service_categories:read'],
          featurePath: 'reservations'
        },
        children: [
          {
            path: '',
            name: 'service-category',
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:service_categories:read'],
              featurePath: 'reservations'
            },
            component: () =>
              import(
                /* webpackChunkName: "reservations_service_category_all" */ '@th/views/reservations/service-category/all'
              )
          },
          {
            path: 'new',
            name: 'category-new',
            component: () =>
              import(
                /* webpackChunkName: "reservations_service_category_edit" */ '@th/views/reservations/service-category/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:service_categories:create'],
              featurePath: 'reservations'
            }
          },
          {
            path: ':id',
            redirect: { name: 'service-category-edit' }
          },
          {
            path: ':id/edit',
            name: 'service-category-edit',
            component: () =>
              import(
                /* webpackChunkName: "reservations_service_category_edit" */ '@th/views/reservations/service-category/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:service_categories:update'],
              featurePath: 'reservations'
            }
          }
        ]
      },
      {
        path: 'services',
        component: () =>
          import(
            /* webpackChunkName: "reservations" */ '@th/views/reservations'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['reservations:services:read'],
          featurePath: 'reservations'
        },
        children: [
          {
            path: '',
            name: 'services',
            component: () =>
              import(
                /* webpackChunkName: "reservations_services_all" */ '@th/views/reservations/services/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:services:read'],
              featurePath: 'reservations'
            }
          },
          {
            path: 'new',
            name: 'services-new',
            component: () =>
              import(
                /* webpackChunkName: "reservations_services_edit" */ '@th/views/reservations/services/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:services:create'],
              featurePath: 'reservations'
            }
          },
          {
            path: ':id',
            redirect: { name: 'services-edit' }
          },
          {
            path: ':id/edit',
            name: 'services-edit',
            component: () =>
              import(
                /* webpackChunkName: "reservations_services_edit" */ '@th/views/reservations/services/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:services:update'],
              featurePath: 'reservations'
            }
          }
        ]
      },
      {
        path: 'service_steps',
        component: () => import('@th/views/reservations/index.vue'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['reservations:service_steps:read'],
          featurePath: 'reservations',
          rolloutFlag: FeatureFlags.SERVICE_STEPS
        },
        children: [
          {
            path: '',
            name: 'service-steps',
            component: () =>
              import('@th/views/reservations/service-steps/all.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:service_steps:read'],
              featurePath: 'reservations'
            }
          },
          {
            path: 'new',
            name: 'service-steps-new',
            component: () =>
              import('@th/views/reservations/service-steps/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:service_steps:create'],
              featurePath: 'reservations'
            }
          },
          {
            path: ':id',
            redirect: { name: 'service-steps-edit' }
          },
          {
            path: ':id/edit',
            name: 'service-steps-edit',
            component: () =>
              import('@th/views/reservations/service-steps/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reservations:service_steps:update'],
              featurePath: 'reservations'
            }
          }
        ]
      },
      {
        path: 'calendar',
        name: 'calendar',
        component: () => import('@th/views/reservations/calendar/index.vue')
      },
      noMatch
    ]
  },
  {
    path: '/floor_layouts',
    component: () => import('@th/views/floor-layouts/index.vue'),
    beforeEnter: requireAuth,
    meta: {
      scopes: ['utilities:floor_layouts:read'],
      featurePath: 'gastro.gastro_basic'
    },
    children: [
      {
        path: '',
        name: 'floor-layouts-list',
        component: () => import('@th/views/floor-layouts/all.vue'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['utilities:floor_layouts:read']
        }
      },
      {
        path: 'new',
        name: 'floor-layouts-new',
        component: () => import('@th/views/floor-layouts/edit.vue'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['utilities:floor_layouts:create']
        }
      },
      {
        path: ':id',
        redirect: { name: 'floor-layouts-edit' }
      },
      {
        path: ':id/edit',
        name: 'floor-layouts-edit',
        component: () => import('@th/views/floor-layouts/edit.vue'),
        beforeEnter: requireAuth,
        meta: {
          scopes: [
            'utilities:floor_layouts:read',
            'utilities:floor_layouts:update'
          ]
        }
      },
      noMatch
    ]
  },
  {
    path: '/products',
    component: () =>
      import(/* webpackChunkName: "products" */ '@th/views/products'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/products'])
      },
      {
        path: 'manager',
        component: () =>
          import(/* webpackChunkName: "products" */ '@th/views/products'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:read'],
          featurePath: 'products.manager'
        },
        children: [
          {
            path: '',
            name: 'products-manager',
            component: () =>
              import(
                /* webpackChunkName: "products_all" */ '@th/views/products/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:read']
            }
          },
          {
            path: 'bulk_edit',
            name: 'products-bulk-edit',
            component: () =>
              import(
                /* webpackChunkName: "products_bulk_edit" */ '@th/views/products/components/bulk-edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:update']
            }
          },
          {
            path: 'duplicate',
            name: 'products-duplicate',
            component: () =>
              import(
                /* webpackChunkName: "products_duplicate" */ '@th/views/products/components/duplicate'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:update']
            }
          },
          {
            path: 'duplicate',
            name: 'products-duplicate',
            component: () =>
              import(
                /* webpackChunkName: "products_duplicate" */ '@th/views/products/components/duplicate'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:update']
            }
          },
          {
            path: 'new',
            name: 'products-new',
            component: () =>
              import(
                /* webpackChunkName: "products_edit_v2" */ '@th/views/products/v2/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'products-edit' }
          },
          {
            path: ':id/edit',
            name: 'products-edit',
            component: () =>
              import(
                /* webpackChunkName: "products_edit_v2" */ '@th/views/products/v2/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:update']
            }
          },
          {
            path: 'custom_dashboards/:name',
            name: 'products-custom-dashboards',
            component: CustomDashboardsWrap,
            beforeEnter: requireAuth,
            meta: {
              scopes: ['utilities:custom_dashboard:read']
            }
          },
          noMatch
        ]
      },
      {
        path: 'product_groups',
        component: () =>
          import(
            /* webpackChunkName: "product_groups" */ '@th/views/product-groups'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:product_groups:read'],
          featurePath: 'products.product_groups'
        },
        children: [
          {
            path: '',
            name: 'products-product-groups-list',
            component: () =>
              import(
                /* webpackChunkName: "products-product-groups-list" */ '@th/views/product-groups/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:product_groups:read']
            }
          },
          {
            path: 'new',
            name: 'products-product-groups-new',
            component: () =>
              import(
                /* webpackChunkName: "products-product-group-edit" */ '@th/views/product-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:product_groups:create']
            }
          },
          {
            path: ':id',
            name: 'products-product-group-display',
            redirect: { name: 'products-product-groups-edit' },
            scopes: ['products:product_groups:read']
          },
          {
            path: ':id/edit',
            name: 'products-product-groups-edit',
            component: () =>
              import(
                /* webpackChunkName: "products-product-group-edit" */ '@th/views/product-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:product_groups:update']
            }
          }
        ]
      },
      {
        path: 'product_templates',
        name: 'products-product-templates',
        component: () =>
          import(
            /* webpackChunkName: "product_templates" */ '@th/views/product-templates'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:product_templates:read'],
          featurePath: 'products.product_templates'
        },
        children: [
          {
            path: '',
            name: 'products-product-templates-all',
            component: () =>
              import(
                /* webpackChunkName: "products-product-templates-all" */ '@th/views/product-templates/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:product_templates:read']
            }
          },
          {
            path: '/products/product_templates/:id',
            name: 'products-product-templates-edit',
            component: () =>
              import(
                /* webpackChunkName: "products-product-templates-edit" */ '@th/views/product-templates/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:product_templates:update']
            }
          }
        ]
      },
      {
        path: 'product_service_questions',
        name: 'products-service-questions',
        component: () =>
          import(
            /* webpackChunkName: "product-services_questions" */ '@th/views/product-services/'
          ),
        meta: {
          scopes: ['products:service_questions:read'],
          featurePath: 'products.service.questions'
        },
        children: [
          {
            path: '',
            name: 'products-service-questions-list',
            component: () =>
              import(
                /* webpackChunkName: "product-services_questions" */ '@th/views/product-services/questions/all'
              ),
            meta: {
              scopes: ['products:service_questions:read']
            }
          },
          {
            path: 'new',
            name: 'products-service-questions-new',
            component: () =>
              import(
                /* webpackChunkName: "product-services_questions" */ '@th/views/product-services/questions/edit'
              ),
            meta: {
              scopes: ['products:service_questions:create']
            }
          },
          {
            path: ':id',
            name: 'products-service-questions-edit',
            component: () =>
              import(
                /* webpackChunkName: "product-services_questions" */ '@th/views/product-services/questions/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:service_questions:update']
            }
          }
        ]
      },
      {
        path: 'discounts',
        component: () =>
          import(/* webpackChunkName: "discounts" */ '@th/views/discounts'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:discounts:read'],
          featurePath: 'products.discounts'
        },
        children: [
          {
            path: '',
            name: 'discounts-list',
            component: () =>
              import(
                /* webpackChunkName: "discounts" */ '@th/views/discounts/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:discounts:read']
            }
          },
          // V2
          {
            path: 'new',
            name: 'discounts-new',
            component: () =>
              import(
                /* webpackChunkName: "discounts_edit" */ '@th/views/discounts/v2/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:discounts:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'discount-edit' }
          },
          {
            path: ':id/edit',
            name: 'discount-edit',
            component: () =>
              import(
                /* webpackChunkName: "discounts_edit" */ '@th/views/discounts/v2/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:discounts:update']
            }
          },
          // V1
          {
            path: 'new-v1',
            name: 'discounts-new-v1',
            component: () =>
              import(
                /* webpackChunkName: "discounts_edit_v1" */ '@th/views/discounts/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:discounts:create']
            }
          },
          {
            path: ':id/edit-v1',
            name: 'discount-edit-v1',
            component: () =>
              import(
                /* webpackChunkName: "discounts_edit_v1" */ '@th/views/discounts/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:discounts:update']
            }
          },
          noMatch
        ]
      },
      {
        path: 'pricebooks',
        component: () =>
          import(
            /* webpackChunkName: "pricebooks_list" */ '@th/views/pricebooks'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:pricebooks:read'],
          featurePath: 'products.pricebooks'
        },
        children: [
          {
            path: '',
            name: 'pricebooks-list',
            component: () =>
              import(
                /* webpackChunkName: "pricebooks" */ '@th/views/pricebooks/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:pricebooks:read']
            }
          },
          {
            path: 'new',
            name: 'pricebooks-new',
            component: () =>
              import(
                /* webpackChunkName: "pricebooks_edit" */ '@th/views/pricebooks/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:pricebooks:create']
            }
          },
          {
            path: ':id/edit',
            name: 'pricebook-edit',
            component: () =>
              import(
                /* webpackChunkName: "pricebooks_edit" */ '@th/views/pricebooks/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:pricebooks:update']
            }
          },
          {
            path: ':id',
            redirect: { name: 'pricebook-edit' }
          },
          noMatch
        ]
      },
      {
        path: 'promotions',
        component: () =>
          import(/* webpackChunkName: "promotions" */ '@th/views/promotions'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:promotions:read']
        },
        children: [
          {
            path: '',
            name: 'promotions-list',
            component: () =>
              import(
                /* webpackChunkName: "promotions" */ '@th/views/promotions/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:promotions:read'],
              featurePath: 'products.promotions'
            }
          },
          {
            path: 'new',
            name: 'promotion-new',
            component: () =>
              import(
                /* webpackChunkName: "promotion_edit" */ '@th/views/promotions/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:promotions:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'promotion-edit' }
          },
          {
            path: ':id/edit',
            name: 'promotion-edit',
            component: () =>
              import(
                /* webpackChunkName: "promotion_edit" */ '@th/views/promotions/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:promotions:update']
            }
          },
          noMatch
        ]
      },
      {
        path: 'tags',
        name: 'products-tags',
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:tags:read'],
          featurePath: 'products.tags'
        },
        children: [
          {
            path: '',
            name: 'products-tags-all',
            component: () =>
              import(/* webpackChunkName: "tags" */ '@th/views/tags'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:tags:read'],
              featurePath: 'products.tags'
            }
          },
          {
            path: 'new',
            name: 'products-tags-new',
            component: () =>
              import(/* webpackChunkName: "tags" */ '@th/views/tags'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:tags:create']
            }
          },
          {
            path: ':id',
            name: 'products-tags-edit',
            component: () =>
              import(/* webpackChunkName: "tags" */ '@th/views/tags'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:tags:update']
            }
          }
        ]
      },
      {
        path: 'addon-groups',
        name: 'products-addon-groups',
        component: () =>
          import(
            /* webpackChunkName: "product_addon_groups" */ '@th/views/product-addon-groups'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['products:addon_groups:read'],
          featurePath: 'products.addon_groups'
        },
        children: [
          {
            path: '',
            name: 'products-addon-groups-list',
            component: () =>
              import(
                /* webpackChunkName: "product_addon_groups" */ '@th/views/product-addon-groups/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:addon_groups:read']
            }
          },
          {
            path: 'new',
            name: 'products-addon-groups-new',
            component: () =>
              import(
                /* webpackChunkName: "product_addon_groups" */ '@th/views/product-addon-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:addon_groups:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'products-addon-groups-edit' }
          },
          {
            path: ':id/edit',
            name: 'products-addon-groups-edit',
            component: () =>
              import(
                /* webpackChunkName: "product_addon_groups" */ '@th/views/product-addon-groups/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['products:addon_groups:update']
            }
          },
          noMatch
        ]
      }
    ]
  },
  {
    path: '/inventory',
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/inventory'])
      },
      // Stock manager
      {
        path: 'stock_manager',
        beforeEnter: requireAuth,
        children: [
          {
            path: '',
            name: 'ims-stock-manager',
            component: () =>
              import(
                /* webpackChunkName: "ims_stock-manager" */ '@th/views/ims/v2/index'
              )
          },
          {
            path: 'goods_in',
            name: 'ims-goods-in',
            component: () =>
              import(
                /* webpackChunkName: "ims_goods-book-in" */ '@th/views/ims/goods-book-in'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['inventory:goods_in:create'],
              featurePath: 'inventory.stock_manager.goods_in'
            }
          },
          {
            path: 'goods_out',
            name: 'ims-goods-out',
            component: () =>
              import(
                /* webpackChunkName: "ims_goods-book-out" */ '@th/views/ims/goods-book-out'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['inventory:goods_out:create'],
              featurePath: 'inventory.stock_manager.goods_out'
            }
          },
          {
            path: 'relocation',
            name: 'ims-relocation',
            component: () =>
              import(
                /* webpackChunkName: "ims_relocation" */ '@th/views/ims/relocation'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['inventory:relocation:create'],
              featurePath: 'inventory.stock_manager.relocation'
            }
          }
        ]
      },
      {
        path: 'stock',
        children: [
          {
            path: '',
            name: 'ims-stock',
            component: () =>
              import(/* webpackChunkName: "stock" */ '@th/views/ims/stock'),
            beforeEnter: requireAuth,
            redirect: { name: 'stock-overview' },
            meta: {
              scopes: [
                'inventory:stock_movements:create',
                'inventory:stock_movements:export'
              ],
              featurePath: 'inventory.stock_movements'
            },
            children: [
              {
                path: 'overview',
                name: 'stock-overview',
                component: () =>
                  import(
                    /* webpackChunkName: "stock-overview" */ '@th/views/ims/stock/products'
                  ),
                beforeEnter: requireAuth,
                meta: {
                  scopes: [
                    'inventory:stock_movements:create',
                    'inventory:stock_movements:export'
                  ],
                  featurePath: 'inventory.stock_movements'
                }
              },
              {
                path: 'movements',
                name: 'stock-movements',
                component: () =>
                  import(
                    /* webpackChunkName: "stock-movements" */ '@th/views/ims/stock/stock-movements'
                  ),
                beforeEnter: requireAuth,
                meta: {
                  scopes: [
                    'inventory:stock_movements:create',
                    'inventory:stock_movements:export'
                  ],
                  featurePath: 'inventory.stock_movements'
                }
              },
              {
                path: 'delivery_sheets',
                name: 'stock-delivery-sheets',
                component: () =>
                  import(
                    /* webpackChunkName: "delivery-sheets" */ '@th/views/ims/stock/consignment-notes'
                  ),
                beforeEnter: requireAuth,
                meta: {
                  scopes: [
                    'inventory:stock_movements:create',
                    'inventory:stock_movements:export'
                  ],
                  featurePath: 'inventory.stock_movements'
                }
              }
            ]
          }
        ]
      },
      {
        path: 'stock_notifications',
        beforeEnter: requireAuth,
        meta: {
          scopes: [],
          featurePath: 'inventory.stock_notifications'
        },
        children: [
          {
            path: '',
            name: 'ims-stock_notifications',
            component: () =>
              import(
                /* webpackChunkName: "ims-stock_notifications" */ '@th/views/ims/stock-notifications'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: [],
              featurePath: 'inventory.stock_notifications'
            }
          },
          {
            path: 'new',
            name: 'ims-stock_notifications_new',
            component: () =>
              import(
                /* webpackChunkName: "ims-stock_notifications_edit" */ '@th/views/ims/stock-notifications/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: [],
              featurePath: 'inventory.stock_notifications'
            }
          },
          {
            path: ':id',
            name: 'ims-stock_notifications_edit',
            component: () =>
              import(
                /* webpackChunkName: "ims-stock_notifications_edit" */ '@th/views/ims/stock-notifications/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: [],
              featurePath: 'inventory.stock_notifications'
            }
          }
        ]
      },
      {
        path: 'reasons',
        component: () =>
          import(/* webpackChunkName: "reasons" */ '@th/views/ims/reasons'),
        beforeEnter: requireAuth,
        meta: {
          featurePath: 'inventory.reasons'
        },
        children: [
          {
            path: '',
            name: 'reasons-list',
            component: () =>
              import(
                /* webpackChunkName: "reasons" */ '@th/views/ims/reasons/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['inventory:reasons:read']
            }
          },
          {
            path: 'new',
            name: 'reasons-new',
            component: () =>
              import(
                /* webpackChunkName: "reasons_edit" */ '@th/views/ims/reasons/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['inventory:reasons:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'reasons-edit' }
          },
          {
            path: ':id/edit',
            name: 'reasons-edit',
            component: () =>
              import(
                /* webpackChunkName: "reasons_edit" */ '@th/views/ims/reasons/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['inventory:reasons:update']
            }
          },
          noMatch
        ]
      },
      {
        path: 'processes',
        component: () =>
          import(
            /* webpackChunkName: "ims_processes" */ '@th/views/ims/processes'
          ),
        beforeEnter: requireAuth,
        meta: {
          featurePath: 'inventory.processes'
        },
        children: [
          {
            path: '',
            name: 'processes-list',
            component: () =>
              import(
                /* webpackChunkName: "processes" */ '@th/views/ims/processes/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['inventory:processes:read']
            }
          },
          {
            path: 'new',
            name: 'processes-new',
            component: () =>
              import(
                /* webpackChunkName: "process_edit" */ '@th/views/ims/processes/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['inventory:processes:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'processes-edit' }
          },
          {
            path: ':id/edit',
            name: 'processes-edit',
            component: () =>
              import(
                /* webpackChunkName: "process_edit" */ '@th/views/ims/processes/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['inventory:processes:update']
            }
          },
          noMatch
        ]
      },
      {
        path: 'stock_takings',
        component: () =>
          import(
            /* webpackChunkName: "ims_stock-takings" */ '@th/views/ims/stock-takings'
          ),
        beforeEnter: requireAuth,
        meta: {
          featurePath: 'inventory.stock_takings'
        },
        children: [
          {
            path: '',
            name: 'stock-takings-list',
            component: () =>
              import(
                /* webpackChunkName: "stock_takings" */ '@th/views/ims/stock-takings/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['inventory:stock_takings:read']
            }
          },
          {
            path: 'new',
            name: 'stock-takings-new',
            component: () =>
              import(
                /* webpackChunkName: "stock_takings_edit" */ '@th/views/ims/stock-takings/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['inventory:stock_takings:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'stock-takings-edit' }
          },
          {
            path: ':id/edit',
            name: 'stock-takings-edit',
            component: () =>
              import(
                /* webpackChunkName: "stock_takings_edit" */ '@th/views/ims/stock-takings/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['inventory:stock_takings:update']
            }
          },
          noMatch
        ]
      },
      noMatch
    ]
  },
  {
    path: '/reports',
    component: () =>
      import(/* webpackChunkName: "reports" */ '@th/views/reports'),
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/reports'])
      },
      {
        path: 'financial_accounting',
        component: () =>
          import(
            /* webpackChunkName: "reports_financial_accounting" */ '@th/views/reports/financial-accounting'
          ),
        beforeEnter: requireAuth,
        props: () => ({}),
        meta: {
          scopes: ['reports_financial_accounting']
        },
        children: [
          {
            path: '',
            name: 'reports-financial-accounting',
            redirect: () =>
              getParentRedirectTo(['/reports', 'financial_accounting'])
          },
          {
            path: 'transactions',
            name: 'reports-financial_accounting-transactions',
            component: () =>
              import(
                /* webpackChunkName: "reports-financial_accounting-transactions" */ '@th/views/reports/financial-accounting/transactions'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_financial_accounting:transactions:read'],
              featurePath: 'reports.financial_accounting.transactions'
            }
          },
          {
            path: 'balances',
            name: 'reports-financial_accounting-balances',
            component: () =>
              import(
                /* webpackChunkName: "reports-financial_accounting-balances" */ '@th/views/reports/financial-accounting/balances'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_financial_accounting:balances:read'],
              featurePath: 'reports.financial_accounting.balances'
            }
          },
          {
            path: 'cash_book',
            name: 'reports-financial_accounting-cash_book',
            component: () =>
              import(
                /* webpackChunkName: "reports-financial_accounting-cash_book" */ '@th/views/reports/financial-accounting/cash_book'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_financial_accounting:cash_book:read'],
              featurePath: 'reports.financial_accounting.cash_book'
            }
          },
          {
            path: 'returns',
            name: 'reports-financial_accounting-returns',
            component: () =>
              import(
                /* webpackChunkName: "reports-financial_accounting-returns" */ '@th/views/reports/financial-accounting/returns'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_financial_accounting:returns:read'],
              featurePath: 'reports.financial_accounting.returns'
            }
          },
          {
            path: 'payment_options',
            name: 'reports-financial_accounting-payment_options',
            component: () =>
              import(
                /* webpackChunkName: "reports-financial_accounting-payment_options" */ '@th/views/reports/financial-accounting/payment_options'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_financial_accounting:payment_options:read'],
              featurePath: 'reports.financial_accounting.payment_options'
            }
          },
          {
            path: 'payments',
            name: 'reports-financial_accounting-payments',
            component: () =>
              import(
                /* webpackChunkName: "reports-financial_accounting-payments" */ '@th/views/reports/financial-accounting/payments'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_financial_accounting:payments:read'],
              featurePath: 'reports.financial_accounting.payments'
            }
          },
          {
            path: 'vat',
            name: 'reports-financial_accounting-vat',
            component: () =>
              import(
                /* webpackChunkName: "reports-financial_accounting-vat" */ '@th/views/reports/financial-accounting/vat'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_financial_accounting:vat:read'],
              featurePath: 'reports.financial_accounting.vat'
            }
          },
          {
            path: 'counting_protocols',
            name: 'counting-protocols',
            component: () =>
              import(
                /* webpackChunkName: "reports-financial_accounting-counting_protocols" */ '@th/views/reports/financial-accounting/counting-protocols'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_financial_accounting:counting_protocols:read'],
              featurePath: 'reports.financial_accounting.counting_protocols'
            }
          },
          noMatch
        ]
      },
      {
        path: 'statistics',
        component: () =>
          import(
            /* webpackChunkName: "reports_statistics" */ '@th/views/reports/statistics'
          ),
        beforeEnter: requireAuth,
        props: () => ({}),
        meta: {
          scopes: ['reports_statistics']
        },
        children: [
          {
            path: '',
            name: 'reports-statistics',
            redirect: () => getParentRedirectTo(['/reports', 'statistics'])
          },
          {
            path: 'revenues',
            name: 'reports-statistics-revenues',
            component: () =>
              import(
                /* webpackChunkName: "reports-statistics-revenues" */ '@th/views/reports/statistics/revenues'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_statistics:revenues:read'],
              featurePath: 'reports.statistics.revenues'
            }
          },
          {
            path: 'gastro-reservations',
            name: 'reports-statistics-gastro-reservations',
            component: () =>
              import('@th/views/reports/statistics/gastro-reservations/index.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_statistics:gastro_reservations:read'],
              featurePath: 'reports.statistics.gastro_reservations'
            }
          },
          {
            path: 'products',
            name: 'reports-statistics-products',
            component: () =>
              import(
                /* webpackChunkName: "reports-statistics-product" */ '@th/views/reports/statistics/products'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_statistics:products:read'],
              featurePath: 'reports.statistics.products'
            }
          },
          {
            path: 'product_groups',
            name: 'reports-statistics-product-groups',
            component: () =>
              import(
                /* webpackChunkName: "reports-statistics-product-groups" */ '@th/views/reports/statistics/product-groups'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_statistics:product_groups:read'],
              featurePath: 'reports.statistics.product_groups'
            }
          },
          {
            path: 'staff',
            name: 'reports-statistics-staff',
            component: () =>
              import(
                /* webpackChunkName: "reports-statistics-staff" */ '@th/views/reports/statistics/staff'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_statistics:staff:read'],
              featurePath: 'reports.statistics.staff'
            }
          },
          {
            path: 'customers',
            name: 'reports-statistics-customers',
            component: () =>
              import(
                /* webpackChunkName: "reports-statistics-customers" */ '@th/views/reports/statistics/customers'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_statistics:customers:read'],
              featurePath: 'reports.statistics.customers'
            }
          },
          {
            path: 'sold_cart_items',
            name: 'reports-statistics-sold-cart-items',
            component: () =>
              import(
                /* webpackChunkName: "reports-statistics-sold-cart-items" */ '@th/views/reports/statistics/sold-cart-items'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_statistics:sold_cart_items:read'],
              featurePath: 'reports.statistics.sold_cart_items'
            }
          },
          {
            path: 'vouchers',
            name: 'reports-statistics-vouchers',
            component: () =>
              import(
                /* webpackChunkName: "reports-statistics-vouchers" */ '@th/views/reports/statistics/vouchers'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_statistics:vouchers:read'],
              featurePath: 'reports.statistics.vouchers'
            }
          },
          {
            path: 'discounts',
            name: 'reports-statistics-discounts',
            component: () =>
              import(
                /* webpackChunkName: "reports-statistics-discounts" */ '@th/views/reports/statistics/discounts'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_statistics:discounts:read'],
              featurePath: 'reports.statistics.discounts'
            }
          },
          {
            path: 'stock',
            name: 'reports-statistics-stock',
            component: () =>
              import(
                /* webpackChunkName: "reports-statistics-stock" */ '@th/views/reports/statistics/stock'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_statistics:stocks:read'],
              featurePath: 'reports.statistics.stocks'
            }
          },
          {
            path: 'inventory',
            name: 'reports-statistics-inventory',
            component: () =>
              import(
                /* webpackChunkName: "reports-statistics-inventory" */ '@th/views/reports/statistics/inventory'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['reports_statistics:inventory:read'],
              featurePath: 'reports.statistics.inventory'
            }
          }
        ]
      },
      {
        path: 'exports_manager',
        name: 'reports-exports-manager',
        component: () =>
          import(
            /* webpackChunkName: "reports-exports-manager" */ '@th/views/reports/exports-manager'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: [
            'reports_exports_manager:financial:gobd',
            'reports_exports_manager:financial:datev',
            'reports_exports_manager:financial:safe_logs'
          ],
          featurePath: 'reports.exports'
        }
      },
      {
        path: 'custom_dashboards/:name',
        name: 'reports-custom-dashboards',
        component: CustomDashboardsWrap,
        beforeEnter: requireAuth,
        meta: {
          scopes: ['utilities:custom_dashboard:read'],
          featurePath: 'utilities.custom_dashboard'
        }
      }
    ]
  },
  {
    path: '/loyalty',
    component: () => import('@th/views/loyalty/index.vue'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/loyalty'])
      },
      {
        path: 'vouchers',
        component: () => import('@th/views/loyalty/vouchers/index.vue'),
        beforeEnter: requireAuth,
        children: [
          {
            path: '',
            redirect: () => getParentRedirectTo(['/loyalty', 'vouchers'])
          },
          {
            path: 'manager',
            component: () =>
              import('@th/views/loyalty/vouchers/vouchers-manager/index.vue'),
            meta: {
              scopes: ['loyalty:read'],
              featurePath: 'loyalty.vouchers'
            },
            children: [
              {
                path: '',
                name: 'vouchers-list',
                meta: {
                  scopes: ['loyalty:read']
                },
                component: () =>
                  import('@th/views/loyalty/vouchers/vouchers-manager/all.vue')
              }
            ]
          },
          {
            path: 'voucher_systems',
            component: () =>
              import('@th/views/loyalty/vouchers/voucher-systems/index.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['loyalty:voucher_systems:read'],
              featurePath: 'loyalty.voucher_systems'
            },
            children: [
              {
                path: '',
                name: 'voucher-systems-list',
                component: () =>
                  import('@th/views/loyalty/vouchers/voucher-systems/all.vue'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['loyalty:voucher_systems:read']
                }
              },
              noMatch
            ]
          },
          {
            path: 'external_systems',
            component: () =>
              import('@th/views/loyalty/vouchers/external-systems/index.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['loyalty:voucher_systems:read'],
              featurePath: 'loyalty.voucher_systems'
            },
            children: [
              {
                path: '',
                name: 'external-systems-list',
                component: () =>
                  import('@th/views/loyalty/vouchers/external-systems/all.vue'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['loyalty:voucher_systems:read']
                }
              },
              {
                path: 'new',
                name: 'external-systems-new',
                component: () =>
                  import('@th/views/loyalty/vouchers/external-systems/edit.vue'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['loyalty:voucher_systems:create']
                }
              },
              {
                path: ':id',
                redirect: { name: 'external-systems-edit' }
              },
              {
                path: ':id/edit',
                name: 'external-systems-edit',
                component: () =>
                  import('@th/views/loyalty/vouchers/external-systems/edit.vue'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['loyalty:voucher_systems:update']
                }
              },
              noMatch
            ]
          }
        ]
      },
      {
        path: 'vouchers/manager',
        meta: {
          scopes: ['loyalty:read'],
          featurePath: 'loyalty.vouchers'
        },
        children: [
          //V1
          {
            path: 'new-v1',
            name: 'voucher-new-v1',
            component: () =>
              import('@th/views/loyalty/vouchers/vouchers-manager/edit.vue'),
            props: (route) => ({
              isNew: true,
              currentDefaultCurrency: route.params.defaultCurrency
            }),
            meta: {
              beforeResolve(routeTo) {
                routeTo.params.defaultCurrency =
                  useConfigurationStore().defaultCurrency
              },
              scopes: ['loyalty:create']
            }
          },
          {
            path: ':id/edit-v1',
            name: 'voucher-edit-v1',
            component: () =>
              import('@th/views/loyalty/vouchers/vouchers-manager/edit.vue'),
            props: () => ({ isNew: false }),
            meta: {
              scopes: ['loyalty:update']
            }
          },
          // V2
          {
            path: 'new',
            name: 'voucher-new',
            component: () =>
              import('@th/views/loyalty/vouchers/vouchers-manager-v2/edit.vue'),
            meta: {
              scopes: ['loyalty:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'voucher-edit' }
          },
          {
            path: ':id/edit',
            name: 'voucher-edit',
            component: () =>
              import('@th/views/loyalty/vouchers/vouchers-manager-v2/edit.vue'),
            props: () => ({ isNew: false }),
            meta: {
              scopes: ['loyalty:update', 'loyalty:read']
            }
          }
        ]
      },
      {
        path: 'vouchers/voucher_systems',
        beforeEnter: requireAuth,
        meta: {
          scopes: ['loyalty:voucher_systems:read'],
          featurePath: 'loyalty.voucher_systems'
        },
        children: [
          //V1
          {
            path: 'new-v1',
            name: 'voucher-systems-new-v1',
            component: () =>
              import('@th/views/loyalty/vouchers/voucher-systems/edit.vue'),
            beforeEnter: requireAuth,
            props: (route) => ({
              isNew: true,
              currentDefaultCurrency: route.params.defaultCurrency
            }),
            meta: {
              beforeResolve(routeTo) {
                routeTo.params.defaultCurrency =
                  useConfigurationStore().defaultCurrency
              },
              scopes: ['loyalty:voucher_systems:create']
            }
          },
          {
            path: ':id/edit-v1',
            name: 'voucher-systems-edit-v1',
            component: () =>
              import('@th/views/loyalty/vouchers/voucher-systems/edit.vue'),
            props: () => ({ isNew: false }),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['loyalty:voucher_systems:update']
            }
          },
          // V2
          {
            path: 'new',
            name: 'voucher-systems-new',
            component: () =>
              import('@th/views/loyalty/vouchers/voucher-systems-v2/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['loyalty:voucher_systems:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'voucher-systems-edit' }
          },
          {
            path: ':id/edit',
            name: 'voucher-systems-edit',
            component: () =>
              import('@th/views/loyalty/vouchers/voucher-systems-v2/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: [
                'loyalty:voucher_systems:update',
                'loyalty:voucher_systems:read'
              ]
            }
          },
          noMatch
        ]
      },
      {
        path: 'vouchers/external_systems',
        beforeEnter: requireAuth,
        meta: {
          scopes: ['loyalty:voucher_systems:read'],
          featurePath: 'loyalty.voucher_systems'
        },
        children: [
          {
            path: 'new',
            name: 'external-systems-new',
            component: () =>
              import('@th/views/loyalty/vouchers/external-systems/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['loyalty:voucher_systems:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'external-systems-edit' }
          },
          {
            path: ':id/edit',
            name: 'external-systems-edit',
            component: () =>
              import('@th/views/loyalty/vouchers/external-systems/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['loyalty:voucher_systems:update']
            }
          },
          noMatch
        ]
      },
      {
        path: 'logs',
        component: Defaulter,
        beforeEnter: requireAuth,
        meta: {
          scopes: ['loyalty:logs'],
          featurePath: 'loyalty.vouchers'
        },
        children: [
          {
            path: '',
            name: 'voucher-logs',
            component: () =>
              import(
                /* webpackChunkName: "voucher_logs" */ '@th/views/loyalty/voucher-logs/all'
              ),
            meta: {
              scopes: ['loyalty:logs']
            }
          },
          noMatch
        ]
      },
      {
        path: 'abocard_systems',
        component: () => import('@th/views/loyalty/abocard-systems/index.vue'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['loyalty:abocard_systems:read'],
          featurePath: 'loyalty.abocard'
        },
        children: [
          {
            path: '',
            name: 'abocard-systems-list',
            component: () =>
              import('@th/views/loyalty/abocard-systems/all.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['loyalty:abocard_systems:read']
            }
          },
          {
            path: 'new',
            name: 'abocard-systems-new',
            component: () =>
              import('@th/views/loyalty/abocard-systems/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['loyalty:abocard_systems:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'abocard-systems-edit' }
          },
          {
            path: ':id/edit',
            name: 'abocard-systems-edit',
            component: () =>
              import('@th/views/loyalty/abocard-systems/edit.vue'),
            beforeEnter: requireAuth,
            meta: {
              scopes: [
                'loyalty:abocard_systems:update',
                'loyalty:abocard_systems:read'
              ]
            }
          },
          noMatch
        ]
      },
      noMatch
    ]
  },
  {
    path: '/user_profile',
    component: () =>
      import(/* webpackChunkName: "user_profile" */ '@th/views/user-profile'),
    beforeEnter: requireAuth,
    meta: {
      async beforeResolve() {
        const authStore = useAuthStore()
        const staff = !!safeGet(authStore, 'staff')
        const isSubUser = !!safeGet(authStore, 'thUserId')
        if (isSubUser && staff) {
          return
        }
        return '/404'
      }
    },
    children: [
      {
        path: '',
        name: 'user-profile',
        component: () =>
          import(
            /* webpackChunkName: "user-profile" */ '@th/views/user-profile/main'
          ),
        children: [
          {
            path: 'sales',
            name: 'sales',
            component: () =>
              import(
                /* webpackChunkName: "user-profile" */ '@th/views/user-profile/main'
              )
          },
          {
            path: 'hours',
            name: 'hours',
            component: () =>
              import(
                /* webpackChunkName: "user-profile" */ '@th/views/user-profile/main'
              )
          }
        ],
        beforeEnter: requireAuth
      },
      noMatch
    ]
  },
  {
    path: '/accounting',
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        component: () => import('@th/views/accounting'),
        children: [
          {
            path: '',
            name: 'accounting',
            redirect: () => getParentRedirectTo(['/accounting'])
          },
          {
            path: 'expense_accounts',
            name: 'accounting-expense-accounts',
            beforeEnter: requireAuth,
            meta: {
              scopes: ['accounting:expense_accounts:read'],
              featurePath: 'accounting.expense_accounts'
            },
            children: [
              {
                path: '',
                name: 'accounting-expense-accounts-default',
                component: () =>
                  import('@th/views/accounting/expense-accounts'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['accounting:expense_accounts:read'],
                  featurePath: 'accounting.expense_accounts'
                }
              },
              {
                path: 'new',
                name: 'accounting-expense-accounts-edit',
                component: () =>
                  import('@th/views/accounting/expense-accounts'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['accounting:expense_accounts:read']
                }
              },
              {
                path: ':id',
                name: 'accounting-expense-accounts-edit',
                component: () =>
                  import('@th/views/accounting/expense-accounts'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['accounting:expense_accounts:read']
                }
              }
            ]
          },
          {
            path: 'taxes',
            name: 'accounting-taxes',
            meta: {
              scopes: ['accounting:taxes:read'],
              featurePath: 'accounting.taxes'
            },
            children: [
              {
                path: '',
                name: 'accounting-taxes-default',
                component: () => import('@th/views/accounting/taxes'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['accounting:taxes:read'],
                  featurePath: 'accounting.taxes'
                }
              },
              {
                path: 'new',
                name: 'accounting-taxes-edit',
                component: () => import('@th/views/accounting/taxes'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['accounting:taxes:read']
                }
              },
              {
                path: ':id',
                name: 'accounting-taxes-edit',
                component: () => import('@th/views/accounting/taxes'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['accounting:taxes:read']
                }
              }
            ]
          },
          {
            path: 'accounts',
            name: 'accounting-accounts',
            meta: {
              scopes: ['accounting:revenue_accounts:read'],
              featurePath: 'accounting.accounts'
            },
            children: [
              {
                path: '',
                name: 'accounting-accounts-default',
                component: () => import('@th/views/accounting/accounts'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['accounting:revenue_accounts:read']
                }
              },
              {
                path: 'new',
                name: 'accounting-accounts-edit',
                component: () => import('@th/views/accounting/accounts'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['accounting:revenue_accounts:read']
                }
              },
              {
                path: ':id',
                name: 'accounting-accounts-edit',
                component: () => import('@th/views/accounting/accounts'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['accounting:revenue_accounts:read']
                }
              }
            ]
          },

          {
            path: 'payment_options',
            component: () => import('@th/views/accounting/payment-options'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['accounting:payment_options:read'],
              featurePath: 'accounting.payment_options'
            },
            children: [
              {
                path: '',
                name: 'accounting-payment-options',
                component: () =>
                  import('@th/views/accounting/payment-options/all'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['accounting:payment_options:read']
                }
              }
            ]
          },
          {
            path: 'safe_management',
            component: () => import('@th/views/accounting/safe-management'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['accounting:safe_management:read'],
              featurePath: 'accounting.safe_management'
            },
            children: [
              {
                path: '',
                name: 'accounting-safe-management',
                component: () =>
                  import('@th/views/accounting/safe-management/all'),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['accounting:safe_management:read']
                }
              }
            ]
          },
          noMatch
        ]
      },
      {
        path: 'payment_options',
        component: () =>
          import('@th/views/accounting/payment-options/index.vue'),
        children: [
          {
            path: 'new',
            name: 'payment-options-new',
            component: () =>
              import('@th/views/accounting/payment-options/edit.vue'),
            meta: {
              scopes: ['accounting:payment_options:create'],
              featurePath: 'accounting.payment_options'
            }
          },
          {
            path: ':id',
            name: 'payment-options-edit',
            component: () =>
              import('@th/views/accounting/payment-options/edit.vue'),
            meta: {
              scopes: ['accounting:payment_options:update'],
              featurePath: 'accounting.payment_options'
            }
          }
        ]
      },
      {
        path: 'safe_management',
        component: () =>
          import('@th/views/accounting/safe-management/index.vue'),
        children: [
          {
            path: '/accounting/safe_management/new',
            name: 'accounting-safe-management-new',
            component: () =>
              import('@th/views/accounting/safe-management/edit'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['accounting:safe_management:create'],
              featurePath: 'accounting.safe_management'
            }
          },
          {
            path: '/accounting/safe_management/:id',
            name: 'accounting-safe-management-overview',
            component: () =>
              import('@th/views/accounting/safe-management/safe-overview'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['accounting:safe_management:read'],
              featurePath: 'accounting.safe_management'
            }
          },
          {
            path: '/accounting/safe_management/:id/edit',
            name: 'accounting-safe-management-edit',
            component: () =>
              import('@th/views/accounting/safe-management/edit'),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['accounting:safe_management:update'],
              featurePath: 'accounting.safe_management'
            }
          }
        ]
      }
    ]
  },

  {
    path: '/timetracking',
    component: () =>
      import(/* webpackChunkName: "timetracking" */ '@th/views/timetracking'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        name: 'timetracking-timesheet',
        component: () =>
          import(
            /* webpackChunkName: "promotions" */ '@th/views/timetracking/timesheet'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['timetracking:read'],
          featurePath: 'timetracking'
        }
      },
      noMatch
    ]
  },
  {
    path: '/settings',
    component: () =>
      import(/* webpackChunkName: "settings" */ '@th/views/settings'),
    beforeEnter: requireAuth,
    children: [
      {
        path: '',
        redirect: () => getParentRedirectTo(['/settings'])
      },
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () =>
          import(
            /* webpackChunkName: "settings_dashboard" */ '@th/views/settings/sections/dashboard'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['settings:dashboard:read']
        },
        children: [
          {
            path: '',
            name: 'settings-dashboard',
            redirect: () => getParentRedirectTo(['/settings', 'dashboard'])
          },
          {
            path: 'general',
            name: 'settings-dashboard-general',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_general" */ '@th/views/settings/sections/dashboard/general'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'searches',
            name: 'settings-dashboard-searches',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_searches" */ '@th/views/settings/sections/dashboard/searches'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'api_users',
            component: Defaulter,
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            },
            children: [
              {
                path: '',
                name: 'settings-dashboard-api-users',
                component: () =>
                  import(
                    /* webpackChunkName: "settings_dashboard_api_users" */ '@th/views/settings/sections/dashboard/api_users'
                  ),
                beforeEnter: requireAuth,
                meta: {
                  scopes: ['settings:dashboard:read']
                }
              }
            ]
          },
          {
            path: 'api_keys',
            name: 'settings-dashboard-api-keys',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_api_keys" */ '@th/views/settings/sections/dashboard/api_keys'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'analytics',
            name: 'settings-dashboard-analytics',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_analytics" */ '@th/views/settings/sections/dashboard/analytics'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'custom_dashboards',
            name: 'settings-dashboard-custom-dashboards',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_custom_dashbaords" */ '@th/views/settings/sections/dashboard/custom-dashboards'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          // {
          //   path: 'integrations',
          //   name: 'settings-dashboard-integrations',
          //   component: () =>
          //     import(
          //       /* webpackChunkName: "settings_dashboard_integrations" */ '@th/views/settings/sections/dashboard/integrations'
          //     ),
          //   beforeEnter: requireAuth,
          //   meta: {
          //     scopes: ['settings:dashboard:read']
          //   }
          // },
          {
            path: 'communications',
            name: 'settings-dashboard-communications',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_communications" */ '@th/views/settings/sections/dashboard/communications'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'fiscalization',
            name: 'settings-dashboard-fiscalization',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_communications" */ '@th/views/settings/sections/dashboard/fiscalizations'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'customers',
            name: 'settings-dashboard-customers',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_customers" */ '@th/views/settings/sections/dashboard/customers'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'suppliers',
            name: 'settings-dashboard-suppliers',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_suppliers" */ '@th/views/settings/sections/dashboard/suppliers'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'purchase-orders',
            name: 'settings-dashboard-purchase-orders',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_purchase-orders" */ '@th/views/settings/sections/dashboard/purchase-orders'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'voucher-systems',
            name: 'settings-dashboard-voucher-systems',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_voucher_systems" */ '@th/views/settings/sections/dashboard/voucher-systems'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'products',
            name: 'settings-dashboard-products',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_products" */ '@th/views/settings/sections/dashboard/products'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'staff',
            name: 'settings-dashboard-staff',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_staff" */ '@th/views/settings/sections/dashboard/staff'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'accounting',
            name: 'settings-dashboard-accounting',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_accounting" */ '@th/views/settings/sections/dashboard/accounting'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'trash',
            name: 'settings-dashboard-trash',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_trash" */ '@th/views/settings/sections/dashboard/trash'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read']
            }
          },
          {
            path: 'remote-ordering-api',
            name: 'settings-dashboard-remote-ordering-api',
            component: () =>
              import(
                /* webpackChunkName: "settings_dashboard_remote_ordering_api" */ '@th/views/settings/sections/dashboard/remote-ordering-api/list.vue'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read'],
              featurePath: 'settings.remote_ordering'
            }
          }
        ]
      },
      {
        path: 'remote-ordering-api',
        component: () =>
          import(
            /* webpackChunkName: "settings_remote_ordering_api" */ '@th/views/settings/sections/remote-ordering-api'
          ),
        redirect: { name: 'settings-dashboard-remote-ordering-api' },
        beforeEnter: requireAuth,
        meta: {
          scopes: ['settings:dashboard:read'],
          featurePath: 'settings.remote_ordering'
        },
        children: [
          {
            path: 'new',
            name: 'settings-remote-ordering-api-new',
            component: () =>
              import(
                /* webpackChunkName: "settings_remote_ordering_api_form" */ '@th/views/settings/sections/remote-ordering-api/form.vue'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read'],
              featurePath: 'settings.remote_ordering'
            }
          },
          {
            path: ':id/edit',
            name: 'settings-remote-ordering-api-edit',
            component: () =>
              import(
                /* webpackChunkName: "settings_remote_ordering_api_form" */ '@th/views/settings/sections/remote-ordering-api/form.vue'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:dashboard:read'],
              featurePath: 'settings.remote_ordering'
            }
          }
        ]
      },
      {
        name: 'reservations',
        path: 'reservations',
        component: () =>
          import('@th/views/settings/sections/reservations/beauty/index.vue'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['settings:reservations:read']
        },
        children: [
          {
            path: '',
            name: 'settings-reservations',
            redirect: () => getParentRedirectTo(['/settings', 'reservations'])
          },
          {
            path: 'customization',
            name: 'settings-reservations-customization',
            component: () =>
              import('@th/views/settings/sections/reservations/beauty/customization/index.vue'),
            beforeEnter: requireAuth
          },
          {
            path: 'opening-hours',
            name: 'settings-reservations-opening-hours',
            component: () =>
              import('@th/views/settings/sections/reservations/beauty/opening-hours/index.vue'),
            beforeEnter: requireAuth
          },
          {
            path: 'appointment-reminders',
            name: 'settings-reservations-appointment-reminders',
            component: () =>
              import('@th/views/settings/sections/reservations/beauty/appointment-reminders/index.vue'),
            beforeEnter: requireAuth
          }
        ]
      },
      {
        name: 'gastro-reservations',
        path: 'gastro-reservations',
        component: () =>
          import('@th/views/settings/sections/reservations/gastro/index.vue'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['settings:gastro_reservations:read']
        },
        children: [
          {
            path: '',
            name: 'settings-gastro-reservations',
            redirect: () =>
              getParentRedirectTo(['/settings', 'gastro-reservations'])
          },
          {
            path: 'general',
            name: 'settings-gastro-reservations-general',
            component: () =>
              import('@th/views/settings/sections/reservations/gastro/general-settings/index.vue'),
            beforeEnter: requireAuth
          },
          {
            path: 'online-booking',
            name: 'settings-gastro-reservations-online-booking',
            component: () =>
              import('@th/views/settings/sections/reservations/gastro/online-booking/index.vue'),
            beforeEnter: requireAuth
          },
          {
            path: 'opening-hours',
            name: 'settings-gastro-reservations-opening-hours',
            component: () =>
              import('@th/views/settings/sections/reservations/gastro/opening-hours/index.vue'),
            beforeEnter: requireAuth
          },
          {
            path: 'reminders',
            name: 'settings-gastro-reservations-reminders',
            component: () =>
              import('@th/views/settings/sections/reservations/gastro/reminders/index.vue'),
            beforeEnter: requireAuth
          }
        ]
      },
      {
        name: 'account',
        path: 'account',
        component: () =>
          import(
            /* webpackChunkName: "settings_account" */ '@th/views/settings/sections/account'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['settings:account']
        }
      },
      {
        path: 'timetracking',
        component: () =>
          import(
            /* webpackChunkName: "settings_timetracking" */ '@th/views/settings/sections/timetracking'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['settings:timetracking']
        }
      },
      {
        name: 'pos',
        path: 'pos',
        component: () =>
          import(
            /* webpackChunkName: "settings_pos" */ '@th/views/settings/sections/pos'
          ),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['settings:pos:read']
        },
        children: [
          {
            path: '',
            name: 'settings-pos',
            redirect: () => getParentRedirectTo(['/settings', 'pos'])
          },
          {
            path: 'discounts',
            name: 'settings-pos-discounts',
            component: () =>
              import(
                /* webpackChunkName: "settings_pos_discounts" */ '@th/views/settings/sections/pos/discounts'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:pos:read']
            }
          },
          {
            path: 'change_suggestions',
            name: 'settings-pos-change-suggestions',
            component: () =>
              import(
                /* webpackChunkName: "settings_pos_change_suggestions" */ '@th/views/settings/sections/pos/change-suggestions'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:pos:read']
            }
          },
          {
            path: 'security',
            name: 'settings-pos-security',
            component: () =>
              import(
                /* webpackChunkName: "settings_pos_security" */ '@th/views/settings/sections/pos/security'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:pos:read']
            }
          },
          {
            path: 'search',
            name: 'settings-pos-search',
            component: () =>
              import(
                /* webpackChunkName: "settings_pos_search" */ '@th/views/settings/sections/pos/search'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:pos:read']
            }
          },
          {
            path: 'check_out',
            name: 'settings-pos-check-out',
            component: () =>
              import(
                /* webpackChunkName: "settings_pos_check_out" */ '@th/views/settings/sections/pos/check_out'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:pos:read']
            }
          },
          {
            path: 'gastronomy',
            name: 'settings-pos-gastronomy',
            component: () =>
              import(
                /* webpackChunkName: "settings_pos_gastronomy" */ '@th/views/settings/sections/pos/gastronomy'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:pos:read']
            }
          },
          {
            path: 'financials',
            name: 'settings-pos-financials',
            component: () =>
              import(
                /* webpackChunkName: "settings_pos_financials" */ '@th/views/settings/sections/pos/financials'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:pos:read']
            }
          },
          {
            path: 'italian_financials',
            name: 'settings-pos-italian-financials',
            component: () =>
              import(
                /* webpackChunkName: "settings_pos_italian_financials" */ '@th/views/settings/sections/pos/italian_financials'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:pos:read']
            }
          },
          {
            path: 'receipts',
            name: 'settings-pos-receipts',
            component: () =>
              import(
                /* webpackChunkName: "settings_pos_receipts" */ '@th/views/settings/sections/pos/receipts'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:pos:read']
            }
          },
          {
            path: 'tipping',
            name: 'settings-pos-tipping',
            component: () =>
              import(
                /* webpackChunkName: "settings_pos_tipping" */ '@th/views/settings/sections/pos/tipping'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:pos:read']
            }
          },
          {
            path: 'checkout_question',
            name: 'settings-pos-checkout-question',
            component: () =>
              import(
                /* webpackChunkName: "settings_pos_checkout_question" */ '@th/views/settings/sections/pos/checkout-question'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:pos:read']
            }
          }
        ]
      },
      {
        path: 'users',
        component: Defaulter,
        beforeEnter: requireAuth,
        meta: {
          scopes: ['settings:users:read']
        },
        children: [
          {
            path: '',
            name: 'settings-users',
            component: () =>
              import(
                /* webpackChunkName: "settings_users" */ '@th/views/settings/sections/users/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:users:read']
            }
          },
          {
            path: 'new',
            name: 'settings-users-new',
            component: () =>
              import(
                /* webpackChunkName: "settings_users_edit" */ '@th/views/settings/sections/users/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:users:create']
            }
          },
          {
            path: ':id',
            redirect: { name: 'settings-users-edit' }
          },
          {
            path: ':id/edit',
            name: 'settings-users-edit',
            component: () =>
              import(
                /* webpackChunkName: "settings_users_edit" */ '@th/views/settings/sections/users/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['settings:users:update']
            }
          }
        ]
      },
      {
        path: 'webhooks',
        component: () =>
          import(/* webpackChunkName: "webhooks" */ '@th/views/webhooks'),
        beforeEnter: requireAuth,
        meta: {
          scopes: ['webhooks:read']
        },
        children: [
          {
            path: '',
            name: 'webhooks-list',
            component: () =>
              import(
                /* webpackChunkName: "webhooks-all" */ '@th/views/webhooks/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['webhooks:read']
            }
          },
          {
            path: 'new',
            name: 'webhooks-new',
            component: () =>
              import(
                /* webpackChunkName: "webhooks_edit" */ '@th/views/webhooks/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['webhooks:create']
            }
          },
          {
            path: ':id',
            component: () =>
              import(
                /* webpackChunkName: "webhooks_edit" */ '@th/views/webhooks/edit'
              ),
            beforeEnter: requireAuth
          },
          noMatch
        ]
      },
      {
        path: 'integrations',
        component: () =>
          import(
            /* webpackChunkName: "integrations" */ '@th/views/settings/sections/integrations'
          ),
        beforeEnter: requireAuth,
        children: [
          {
            path: '',
            name: 'integrations-list',
            component: () =>
              import(
                /* webpackChunkName: "integrations-all" */ '@th/views/settings/sections/integrations/all'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['integrations:read']
            }
          },
          {
            path: 'edit',
            name: 'integrations-edit',
            component: () =>
              import(
                /* webpackChunkName: "integrations-edit" */ '@th/views/settings/sections/integrations/edit'
              ),
            beforeEnter: requireAuth,
            meta: {
              scopes: ['integrations:update']
            }
          }
        ]
      },
      noMatch
    ]
  },
  ...commonRoutes,
  noMatch
]

function getParentRedirectTo(pathToChildren) {
  const authStore = useAuthStore()
  const role = safeGet(authStore, 'role')
  const userScopes = safeGet(authStore, 'scopes')

  const { featureLock, featureConfig } = useAppConfigStore()

  return getChildNameByPermissions(
    pathToChildren,
    routes,
    role,
    userScopes,
    featureConfig,
    featureLock
  )
}

export default routes
